import React from 'react';

const Privacy: React.FC = () => {
  return (
    <div className="blueprint-grid min-h-screen bg-white">
      {/* Simple Header */}
      <div className="border-b border-black/[0.08] py-12">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">Legal</span>
          <h1 className="mt-2 font-serif text-4xl tracking-tight text-charcoal md:text-5xl">Privacy Policy</h1>
          <p className="mt-3 font-mono text-xs text-ink-muted">Last updated: October 22, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="card-frame p-8 md:p-12">
          <div className="prose prose-lg max-w-none text-gray-700 space-y-8">
            
            <p className="text-lg leading-relaxed">
              At Steelman, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you use our service.
            </p>
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                1. Information We Collect
              </h2>
              
              <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">Personal Information</h3>
              <p className="leading-relaxed mb-3">
                We may collect information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-6">
                <li>Account information</li>
                <li>Contact details</li>
                <li>Authentication credentials</li>
                <li>Payment information (processed securely through third-party providers)</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-charcoal mt-6 mb-3">Usage Information</h3>
              <p className="leading-relaxed mb-3">
                We may automatically collect certain information about how you use our service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Content you create and interact with</li>
                <li>Usage patterns and preferences</li>
                <li>Technical information about your device</li>
                <li>General location data</li>
                <li>Service usage metrics</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                2. How We Use Your Information
              </h2>
              <p className="leading-relaxed mb-3">
                We may use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Operate and improve our services</li>
                <li>Process your requests and transactions</li>
                <li>Communicate with you about the service</li>
                <li>Provide customer support</li>
                <li>Analyze usage and trends</li>
                <li>Protect against misuse or harmful activity</li>
                <li>Enhance user experience</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                3. Information Sharing
              </h2>
              <p className="leading-relaxed mb-3">
                We do not sell your personal information. We may share your information in certain circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li><strong>Service Providers:</strong> With trusted third-party service providers who assist in 
                operating our platform</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In connection with business transactions such as mergers 
                or acquisitions</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                4. Data Security
              </h2>
              <p className="leading-relaxed">
                We take reasonable measures to protect your information. However, no security system is perfect, 
                and we cannot guarantee absolute security.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                5. Data Retention
              </h2>
              <p className="leading-relaxed">
                We retain your information as needed to provide our services and fulfill legal obligations. 
                You may request deletion of your data in accordance with applicable law.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                6. Your Rights
              </h2>
              <p className="leading-relaxed mb-3">
                Subject to applicable law, you may have certain rights regarding your information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Access your information</li>
                <li>Correct or update your information</li>
                <li>Request deletion of your information</li>
                <li>Manage data processing preferences</li>
                <li>Withdraw consent where applicable</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                7. Cookies and Tracking
              </h2>
              <p className="leading-relaxed">
                We may use cookies and similar technologies to enhance your experience. You can manage your 
                preferences through your browser settings.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                8. Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Our service is not intended for minors. We do not knowingly collect information from individuals 
                under the applicable age of consent. If you believe we have such information, please contact us.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                9. International Data Transfers
              </h2>
              <p className="leading-relaxed">
                Your information may be processed in various jurisdictions. We take appropriate steps to protect 
                your information regardless of where it is processed.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                10. Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy periodically. Continued use of our service after changes 
                constitutes acceptance of the updated policy.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-serif font-bold text-charcoal mb-4 pb-2 border-b border-gray-200">
                11. Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have questions about this Privacy Policy, please contact us on{' '}
                <a 
                  href="https://x.com/steelman_ai" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-steel-blue hover:text-steel-blue-dark font-semibold transition-colors"
                >
                  X/Twitter @steelman_ai
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;

